// Equativ per-audience CPM price (ASB-3872) — replaces the flat hardcoded 1.0 the
// daily export previously sent for every audience/silo. Shared by the two places an
// audience can be activated (or re-priced) to Equativ from the UI: the Audience
// Builder's own Destinations save flow (DestinationsModal) and the modeled-audience
// editor (removed with the V1 builder). Both prompted for a price whenever
// Equativ is selected, prefilled with the current price on an already-active audience
// (ASB-4022). The Audience Manager's Equativ Price column (editable any time) and bulk
// "Push to Destinations" action do not prompt.

// Matches backend's EQUATIV_ACTIVATION_METHOD_ID (cohorts.py). Activation method ids are
// a stable backend contract; the display name ("Equativ") is not — match on this, not on
// name/label.
export const EQUATIV_ACTIVATION_METHOD_ID = 16;

const PRICE_RE = /^\d+(\.\d{1,2})?$/;

/**
 * @param {string|number} id - an activation-method id as it might arrive from an API response.
 * @returns {boolean} whether `id` is the Equativ activation method, tolerating a string id
 *   (some response shapes carry ids as strings — matches the existing GAM id=1 check's own
 *   `d.id === 1 || d.id === "1"` hedge).
 */
export function isEquativMethodId(id) {
	return id === EQUATIV_ACTIVATION_METHOD_ID || id === String(EQUATIV_ACTIVATION_METHOD_ID);
}

/**
 * @param {string|number} value - the raw input value. Empty string/null/undefined is
 *   valid (means "use the default"); the price prompt is optional.
 * @returns {boolean} whether the value is a non-negative number with at most 2 decimal
 *   places, matching the backend's _validateEquativPrice.
 */
export function isValidEquativPrice(value) {
	if (value === "" || value === null || value === undefined) return true;
	return PRICE_RE.test(String(value).trim());
}

// The API always returns this as a bare float (cohorts.equativ_price cast via
// _effectiveEquativPrice) — no fixed decimal places, so 1 / 2.5 / 0.75 / 3 all come back
// looking different from each other. Every other money column in the Manager (Prebid CPM,
// Prebid Revenue) is two-decimal-formatted; this wasn't, so a column of prices read
// inconsistently. Deliberately no currency symbol/style — the API carries no currency for
// this value (it's whatever the publisher's Equativ account itself bills in), so a "$"
// prefix would be a guess.
// useGrouping: false is load-bearing, not cosmetic: since ASB-4022 this formatter also
// prefills the editable price input on an already-active audience (DestinationsModal,
// the modeled editor), and isValidEquativPrice's regex rejects commas — a price >= 1000
// would otherwise prefill as "1,000.00" and immediately fail validation, blocking Save
// until the user manually retyped it (review bot caught this on PR #4577).
const PRICE_FORMATTER = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

/**
 * @param {string|number|null|undefined} value - a price as the API/an edit diff carries it.
 * @returns {string} the value formatted to exactly 2 decimal places (e.g. "1.00", "2.50"),
 *   or "" for null/undefined/empty, or the value's own String() if it isn't a finite number
 *   (defensive — display should never throw on a garbled value).
 */
export function formatEquativPrice(value) {
	if (value === null || value === undefined || value === "") return "";
	const n = Number(value);
	if (!Number.isFinite(n)) return String(value);
	return PRICE_FORMATTER.format(n);
}

/**
 * @param {Array} activationMethods - an activation_methods array as the API/local state
 *   carries it — each entry an object with `id` and, for Equativ, `details.equativ.price`
 *   (ASB-3622 shape). Tolerates a missing/empty array.
 * @returns {string} the Equativ entry's current price, formatted to 2 decimal places, or
 *   "" if Equativ isn't in the list or carries no price yet (not yet activated). Shared by
 *   DestinationsModal and the modeled-audience editor (ASB-4022) so both prefill the same
 *   way and neither re-derives its own id-matching logic.
 */
export function getEquativPriceFromMethods(activationMethods) {
	const equativ = (activationMethods || []).find((m) => isEquativMethodId(m?.id ?? m));
	return formatEquativPrice(equativ?.details?.equativ?.price);
}
